import React, { useState, useEffect } from 'react';
import { BellIcon } from '@heroicons/react/24/solid';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import apiClient from '../services/unifiedApiClient';
import Button from '../design-system/components/Button';
import Input from '../design-system/components/Input';
import { Heading3, Text } from '../design-system/components/Typography';

const T = {
  paper:     '#F7EFD8',
  paperDeep: '#EEE2BF',
  paperSoft: '#FDF7E5',
  card:      '#FFFBEC',
  ink:       '#2E2109',
  ink2:      '#4A3510',
  inkMuted:  '#6E5A38',
  inkFaint:  '#A39068',
  rule:      '#D7C9A1',
  ruleStrong:'#BFAB79',
  orange:    '#EA7C1F',
  orangeDark:'#C25F0E',
  moss:      '#6B7A3E',
  capRed:    '#C14A33',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

const BlogSubscription = ({ variant = 'inline', title = 'Subscribe to our blog' }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [mySubscription, setMySubscription] = useState(null);
  const [showEmailForm, setShowEmailForm] = useState(false);

  const { showToast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchMySubscription();
    }
  }, [user]);

  const fetchMySubscription = async () => {
    try {
      const subscription = await apiClient.get('/blog/my-subscription');
      setMySubscription(subscription);
    } catch (error) {
      // User doesn't have a subscription
      setMySubscription(null);
    }
  };

  const handleSubscribe = async (subscriptionEmail) => {
    setLoading(true);
    try {
      const data = {
        email: subscriptionEmail,
        frequency: 'weekly',
        categories: [],
      };

      await apiClient.post('/blog/subscribe', data);
      showToast('Successfully subscribed! You\'ll receive weekly updates.', 'success');

      if (user) {
        fetchMySubscription();
      } else {
        setShowEmailForm(false);
        setEmail('');
      }
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to subscribe', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    if (!user) return;

    if (window.confirm('Are you sure you want to unsubscribe from blog notifications?')) {
      setLoading(true);
      try {
        await apiClient.delete('/blog/my-subscription');
        showToast('Successfully unsubscribed', 'success');
        setMySubscription(null);
      } catch (error) {
        showToast(error.response?.data?.detail || 'Failed to unsubscribe', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (email.trim()) {
      handleSubscribe(email);
    }
  };

  const handleQuickSubscribe = () => {
    if (user) {
      if (mySubscription) {
        handleUnsubscribe();
      } else {
        handleSubscribe(user.email);
      }
    } else {
      setShowEmailForm(true);
    }
  };

  if (variant === 'compact') {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 0',
        borderTop: `1px dashed ${T.rule}`,
        borderBottom: `1px dashed ${T.rule}`,
        margin: '16px 0',
        fontFamily: T.sans,
      }}>
        <BellIcon style={{
          width: '20px',
          height: '20px',
          color: T.orange,
          flexShrink: 0,
        }} />
        <Text style={{
          flex: 1,
          margin: 0,
          color: T.ink2,
          fontSize: '14px',
          fontFamily: T.sans,
        }}>
          {user && mySubscription
            ? 'You\'re subscribed to blog updates'
            : 'Get notified of new blog posts'
          }
        </Text>
        <button
          onClick={handleQuickSubscribe}
          disabled={loading}
          style={{
            padding: '6px 14px',
            fontSize: '13px',
            fontWeight: 700,
            color: user && mySubscription ? T.inkMuted : '#fff',
            backgroundColor: user && mySubscription ? 'transparent' : T.orange,
            border: user && mySubscription ? `1px solid ${T.rule}` : 'none',
            borderRadius: T.r2,
            cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily: T.sans,
            opacity: loading ? 0.65 : 1,
            transition: 'opacity 0.15s, background-color 0.15s',
          }}
        >
          {user && mySubscription ? 'Subscribed' : 'Subscribe'}
        </button>
      </div>
    );
  }

  return (
    <div style={{
      margin: '24px 0',
      textAlign: 'center',
      background: T.paper,
      borderRadius: T.r4,
      border: `1px solid ${T.rule}`,
      boxShadow: T.sh1,
      padding: '28px 24px',
      fontFamily: T.sans,
    }}>
      <div style={{
        width: '52px',
        height: '52px',
        borderRadius: T.full,
        backgroundColor: `${T.orange}18`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 14px auto',
      }}>
        <BellIcon style={{
          width: '26px',
          height: '26px',
          color: T.orange,
        }} />
      </div>

      <Heading3 style={{
        marginBottom: '8px',
        color: T.ink,
        fontSize: '18px',
        fontWeight: 700,
        fontFamily: T.sans,
      }}>
        {title}
      </Heading3>

      <Text style={{
        marginBottom: '20px',
        color: T.inkMuted,
        maxWidth: '400px',
        margin: `0 auto 20px auto`,
        fontSize: '14px',
        lineHeight: 1.6,
        fontFamily: T.sans,
      }}>
        {user && mySubscription
          ? `You're currently subscribed and will receive ${mySubscription.frequency} updates. Manage your subscription anytime.`
          : 'Stay updated with our latest insights on AI-powered product planning, event modeling, and software development best practices.'
        }
      </Text>

      {user ? (
        // Authenticated user
        <button
          onClick={handleQuickSubscribe}
          disabled={loading}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 22px',
            fontSize: '15px',
            fontWeight: 700,
            color: mySubscription ? T.inkMuted : '#fff',
            backgroundColor: mySubscription ? 'transparent' : T.orange,
            border: mySubscription ? `1px solid ${T.rule}` : 'none',
            borderRadius: T.r3,
            cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily: T.sans,
            opacity: loading ? 0.65 : 1,
            transition: 'opacity 0.15s, background-color 0.15s',
          }}
          onMouseEnter={(e) => {
            if (!loading && !mySubscription) e.currentTarget.style.backgroundColor = T.orangeDark;
          }}
          onMouseLeave={(e) => {
            if (!loading && !mySubscription) e.currentTarget.style.backgroundColor = T.orange;
          }}
        >
          <BellIcon style={{ width: '18px', height: '18px' }} />
          {mySubscription ? 'Manage Subscription' : 'Subscribe Now'}
        </button>
      ) : (
        // Non-authenticated user
        <div>
          {!showEmailForm ? (
            <button
              onClick={() => setShowEmailForm(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 22px',
                fontSize: '15px',
                fontWeight: 700,
                color: '#fff',
                backgroundColor: T.orange,
                border: 'none',
                borderRadius: T.r3,
                cursor: 'pointer',
                fontFamily: T.sans,
                transition: 'background-color 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = T.orangeDark; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = T.orange; }}
            >
              <BellIcon style={{ width: '18px', height: '18px' }} />
              Subscribe Now
            </button>
          ) : (
            <form onSubmit={handleEmailSubmit} style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              maxWidth: '320px',
              margin: '0 auto',
            }}>
              <input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '9px 13px',
                  backgroundColor: T.card,
                  border: `1px solid ${T.rule}`,
                  borderRadius: T.r2,
                  color: T.ink,
                  fontSize: '14px',
                  fontFamily: T.sans,
                  outline: 'none',
                  transition: 'border-color 0.15s',
                }}
                onFocus={(e) => { e.target.style.borderColor = T.orange; }}
                onBlur={(e) => { e.target.style.borderColor = T.rule; }}
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: '9px 16px',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: '#fff',
                    backgroundColor: loading ? `${T.orange}88` : T.orange,
                    border: 'none',
                    borderRadius: T.r2,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontFamily: T.sans,
                    transition: 'background-color 0.15s',
                  }}
                  onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = T.orangeDark; }}
                  onMouseLeave={(e) => { if (!loading) e.currentTarget.style.backgroundColor = T.orange; }}
                >
                  {loading ? 'Subscribing...' : 'Subscribe'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowEmailForm(false)}
                  style={{
                    padding: '9px 16px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: T.inkMuted,
                    backgroundColor: 'transparent',
                    border: `1px solid ${T.rule}`,
                    borderRadius: T.r2,
                    cursor: 'pointer',
                    fontFamily: T.sans,
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default BlogSubscription;
